// 语言/主题偏好协议（与 soda-site/assets/soda-i18n.js 保持一致，两个仓库各持一份实现）
//
// 判定优先级：URL ?lang= → Cookie(.hibuka.com 跨子域共享) → localStorage → 兜底
//   语言兜底 = 浏览器语言（navigator.languages，zh 开头 → 中文，否则英文）
//   主题兜底 = auto（跟随系统深浅色）
// 页面互链时把 ?lang= 拼进 href，跨子域跳转语言不丢。

const LANG_KEY = 'soda-lang';
const THEME_KEY = 'soda-theme';

function readCookie(name) {
  const m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return m ? decodeURIComponent(m.pop()) : null;
}

function writeCookie(name, value) {
  // hibuka.com 下共享给所有子域；本地开发等其它 host 退化为主机自有 cookie
  const domain = /(^|\.)hibuka\.com$/.test(window.location.hostname) ? 'hibuka.com' : null;
  let cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=31536000;SameSite=Lax`;
  if (domain) cookie += `;domain=.${domain}`;
  document.cookie = cookie;
}

function readStore(key) {
  try { return window.localStorage.getItem(key); } catch (e) { return null; }
}

function writeStore(key, value) {
  try { window.localStorage.setItem(key, value); } catch (e) { /* 隐私模式忽略 */ }
}

function validLang(v) { return v === 'zh' || v === 'en' ? v : null; }
function validTheme(v) { return v === 'auto' || v === 'light' || v === 'dark' ? v : null; }

function urlLang() {
  const m = /[?&]lang=([a-zA-Z-]+)/.exec(window.location.search || '');
  if (!m) return null;
  const v = m[1].toLowerCase();
  return v.indexOf('zh') === 0 ? 'zh' : (v === 'en' ? 'en' : null);
}

export function getInitialLang() {
  const url = urlLang();
  if (url) { saveLang(url); return url; }
  const cookie = validLang(readCookie(LANG_KEY));
  if (cookie) return cookie;
  const stored = validLang(readStore(LANG_KEY));
  if (stored) return stored;
  const list = navigator.languages || [navigator.language || ''];
  for (const item of list) {
    if (/^zh/i.test(item || '')) return 'zh';
  }
  return 'en';
}

export function saveLang(lang) {
  writeCookie(LANG_KEY, lang);
  writeStore(LANG_KEY, lang);
}

export function getInitialThemeMode() {
  return validTheme(readCookie(THEME_KEY)) || validTheme(readStore(THEME_KEY)) || 'auto';
}

export function saveThemeMode(mode) {
  writeCookie(THEME_KEY, mode);
  writeStore(THEME_KEY, mode);
}

export function resolveTheme(mode) {
  return mode === 'auto'
    ? (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark')
    : mode;
}

// 给跨页链接补/替换 ?lang=
export function withLang(url, lang) {
  if (!url || url.charAt(0) === '#') return url;
  if (/[?&]lang=/.test(url)) return url.replace(/([?&]lang=)[a-zA-Z-]+/, `$1${lang}`);
  return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'lang=' + lang;
}

// 模板卡片英文文案。接口 _manifest.json 只提供中文，zh 直接用接口值；
// 新增模板时在这里补一条，未命中映射的模板回退接口返回值。
export const templateTextEn = {
  'soda-simple': {
    name: 'SODA Simple',
    description: 'SODA Simple (monolith) — for Spring Boot applications',
  },
  'scoda-cloud': {
    name: 'SODA Cloud',
    description: 'SODA Cloud (multi-module) — for Spring Cloud microservices, with a dedicated Utils module and optional Nacos service registry',
  },
};

// 按界面语言取模板卡片文案，缺失时回退接口返回值
export function tplText(template, lang) {
  const m = lang === 'en' ? templateTextEn[template.id] : null;
  return {
    name: (m && m.name) || template.name,
    description: (m && m.description) || template.description,
  };
}

// 界面文案。en 为对外默认文案口径，zh 为原生文案。
export const strings = {
  zh: {
    title: 'soda 项目生成器',
    navProject: '项目生成器',
    navProduct: '产品介绍 ↗',
    navDocs: '文档 ↗',
    themeAuto: '自动',
    themeLight: '浅',
    themeDark: '深',
    heroTitle: '项目生成器',
    desc: '高效领域驱动设计架构，助力企业级 SpringBoot 项目高效开发，开启高质量工程',
    labelPackage: '包名：',
    labelProject: '项目名：',
    labelTemplate: '模板类型：',
    labelCommentLang: '注释语言：',
    labelBootVersion: 'Spring Boot版本：',
    phPackage: '例如: com.example.demo',
    phProject: '例如: my-project',
    loadingTemplates: '加载模板中...',
    tplLoadFailed: '模板列表加载失败，请刷新重试',
    btnPreview: '预览项目',
    btnPreviewBusy: '预览中...',
    btnSubmit: '生成并下载',
    btnSubmitBusy: '生成中...',
    previewTitle: '项目预览',
    previewTree: '文件结构',
    previewFile: '文件内容: ',
    fullscreen: '放大浏览',
    footerProduct: '产品页',
    footerDocs: '文档',
    footerCopy: '版权所有 © 2026 hibuka',
    errPackageRequired: '包名不能为空',
    errPackageFormat: '包名格式不正确。包名必须：\n• 使用点号(.)分隔\n• 每个部分以字母开头\n• 只能包含小写字母和数字\n• 例如：com.example.myapp',
    errPackageDotEdge: '包名不能以点号开头或结尾',
    errPackageDotSeq: '包名不能包含连续的点号',
    errPackageEmptyPart: '包名的每个部分不能为空',
    errProjectRequired: '项目名称不能为空',
    errProjectFormat: '项目名称格式不正确。项目名称必须：\n• 以字母开头\n• 只能包含字母、数字、连字符(-)和下划线(_)\n• 例如：my-project、my_project、myProject',
    errProjectMin: '项目名称至少需要2个字符',
    errProjectMax: '项目名称不能超过50个字符',
    errCheckPreview: '请检查包名和项目名格式，确保符合要求后再预览',
    errCheckGenerate: '请检查包名和项目名格式，确保符合要求后再生成项目',
    errPreview: '预览失败: ',
    errGenerate: '生成项目失败: ',
    errReadFile: '读取文件失败: ',
    errLoadTemplates: '加载模板列表失败: ',
  },
  en: {
    title: 'soda Project Generator',
    navProject: 'Project Generator',
    navProduct: 'Product ↗',
    navDocs: 'Docs ↗',
    themeAuto: 'Auto',
    themeLight: 'Light',
    themeDark: 'Dark',
    heroTitle: 'Project Generator',
    desc: 'An efficient domain-driven design architecture for enterprise Spring Boot — quality engineering from the start.',
    labelPackage: 'Package name:',
    labelProject: 'Project name:',
    labelTemplate: 'Template:',
    labelCommentLang: 'Comment language:',
    labelBootVersion: 'Spring Boot version:',
    phPackage: 'e.g. com.example.demo',
    phProject: 'e.g. my-project',
    loadingTemplates: 'Loading templates...',
    tplLoadFailed: 'Failed to load templates, please refresh and try again',
    btnPreview: 'Preview',
    btnPreviewBusy: 'Previewing...',
    btnSubmit: 'Generate & Download',
    btnSubmitBusy: 'Generating...',
    previewTitle: 'Project Preview',
    previewTree: 'File Structure',
    previewFile: 'File: ',
    fullscreen: 'Fullscreen',
    footerProduct: 'Product',
    footerDocs: 'Docs',
    footerCopy: 'Copyright © 2026 hibuka',
    errPackageRequired: 'Package name is required',
    errPackageFormat: 'Invalid package name. It must:\n• be dot-separated (e.g. com.example.myapp)\n• start each part with a letter\n• contain only lowercase letters and digits',
    errPackageDotEdge: 'Package name cannot start or end with a dot',
    errPackageDotSeq: 'Package name cannot contain consecutive dots',
    errPackageEmptyPart: 'Package name parts cannot be empty',
    errProjectRequired: 'Project name is required',
    errProjectFormat: 'Invalid project name. It must:\n• start with a letter\n• contain only letters, digits, hyphens (-) and underscores (_)\n• e.g. my-project, my_project, myProject',
    errProjectMin: 'Project name needs at least 2 characters',
    errProjectMax: 'Project name cannot exceed 50 characters',
    errCheckPreview: 'Please fix the package and project name errors before previewing',
    errCheckGenerate: 'Please fix the package and project name errors before generating',
    errPreview: 'Preview failed: ',
    errGenerate: 'Generation failed: ',
    errReadFile: 'Failed to read file: ',
    errLoadTemplates: 'Failed to load templates: ',
  },
};
